import { ConfirmationDialog } from 'components/ConfirmationDialog';
import { ContractOtherCostLimitSpan } from 'components/ContractOtherCostLimitSpan/ContractOtherCostLimitSpan';
import { OtherCostTypeSelect } from 'components/EditOtherForm/OtherCostTypeSelect';
import { ControlledSelectField, ControlledTextField, ControlNumberField } from 'components/Form';
import { ControlledTabs } from 'components/Form/ControlledTab';
import { FormulaEditor, useEditorParseData } from 'components/FormulaEditor';
import { ModalDialog } from 'components/ModalDialog';
import { OtherManualSeasonDistribution } from 'components/OtherManualSeasonDistribution';
import { UploadStatus } from 'components/UploadStatus';
import type { IErrorHandlingRequestState } from 'features/errorHandling/errorHandlingSlice';
import { RequestStatus } from 'features/request/requestSlice';
import type { ConstructionTypeData } from 'gql/graphql';
import { OtherCostCalculationTypeEnum, ProjectMethodologyEnum, WorkOrderStatusEnum } from 'gql/graphql';
import { useProjectData } from 'hooks/useProjectData';
import { useSendGpnAnalyticsEvent } from 'hooks/useSendGpnAnalyticsEvent';
import { useUerContractOtherCostLimitSpanSelectionList } from 'pages/Project/UerProject/Contract/hooks/useUerContractOtherCostLimitSpanSelectionList';
import { useConstructionTypes } from 'pages/Project/UerProject/WorkOrder/hooks/useConstructionTypes';
import { Checkbox, FieldGroup, Grid, GridItem, IconWarning, Informer, RadioGroup, Text, Tooltip } from 'uikit';
import { replacerCost } from 'utils/replacerCost';

import * as R from 'ramda';
import type React from 'react';
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { FormProvider, useForm, useWatch } from 'react-hook-form';
import { useParams } from 'react-router-dom';

import { prepareLimitsData, validateLimitCost } from '../../hooks/prepareLimitsData';
import { getOtherFormulaSourceObjectGql } from '../contractQueries';
import { useUerContractSeasons } from '../hooks/useUerContractSeasons';

import type { TabItem, TypeItem } from './commonData';
import {
    availableChapters,
    CreatingTypes,
    OTHER_SECTION_OR_CHAPTER,
    seasonItems,
    TabsIds,
    tabsItems,
    TYPE_VARIANTS,
} from './commonData';
import { CreateOtherNameSelect } from './CreateOtherNameSelect';
import { useContractOtherCreate } from './hooks/useContractOtherCreate';
import { useGetContractOtherCostTypes } from './hooks/useGetContractOtherCostTypes';
import { useIsUniqueContractOtherCode } from './hooks/useIsUniqueContractOtherCode';

import Styles from './CreateOtherModal.module.css';

const getItemLabel = (item: { label: any }) => item.label;

const requiredMessage = { message: 'Обязательное поле' };
const codeKey = 'key';

const fieldsToCheck = {
    [TabsIds.COMMON]: ['name', 'globalOtherCostId', 'percent', 'rationale', 'otherCostType'],
    [TabsIds.PRICE]: ['cost', 'method'],
    [TabsIds.FORMULA]: ['formula'],
};

export const invisibleStyle: React.CSSProperties = {
    visibility: 'hidden',
    position: 'absolute',
    overflow: 'hidden',
    maxHeight: 0,
};

interface ICreateOtherModal {
    handleClose: () => void;
    creatingType: CreatingTypes;
    currentConstructionTypeId?: number;
    reloadHandler: (id: number) => void;
    methodology: ProjectMethodologyEnum;
    isCondition?: boolean;
    constructionTypes?: ConstructionTypeData[];
    workOrderId?: number;
    workOrderChangeId?: number;
    workOrderObjectId?: number;
    workOrderStatus?: WorkOrderStatusEnum;
    contractId?: number;
    noLimitSpan?: boolean;
    hasWorkOrders?: boolean;
}

export const CreateOtherModal: React.FC<ICreateOtherModal> = (props) => {
    const {
        handleClose,
        creatingType,
        currentConstructionTypeId,
        reloadHandler,
        constructionTypes,
        isCondition,
        methodology,
        workOrderId,
        workOrderStatus,
        workOrderObjectId,
        workOrderChangeId,
        contractId,
        noLimitSpan = false,
        hasWorkOrders = false,
    } = props;

    const title = `Создание прочих ${
        creatingType === CreatingTypes.BOOK ? 'из справочника методик расчета' : 'затрат'
    }`;

    const refTooltip = useRef(null);
    const isCreatingFromCatalog = creatingType === CreatingTypes.BOOK;

    const { id: projectId } = useParams();
    const { projectData } = useProjectData(projectId);

    const { sendGpnAnalyticsEvent } = useSendGpnAnalyticsEvent();

    const form = useForm({
        criteriaMode: 'all',
        shouldUnregister: false,
    });
    const {
        control,
        handleSubmit,
        setError,
        formState: { isDirty, errors, dirtyFields },
        reset,
        setValue,
        clearErrors,
    } = form;

    const formRef = useRef<HTMLFormElement>(null);

    const [activeTab, setActiveTab] = useState<TabItem>(tabsItems[0]);
    const [selectedType, setSelectedType] = useState<TypeItem>(TYPE_VARIANTS[0]);
    const [isOpenTooltip, setIsOpenTooltip] = useState<boolean>(false);
    const [selectedChapterOrSection, setSelectedChapterOrSection] = useState(OTHER_SECTION_OR_CHAPTER[0]);
    const [isOpenWarningDialog, setIsOpenWarningDialog] = useState<boolean>(false);
    const [noOtherCostTypeErrorState, setNoOtherCostTypeErrorState] = useState<
        IErrorHandlingRequestState | undefined
    >();

    const globalOtherCost: {
        calculatingType: OtherCostCalculationTypeEnum;
        id: number;
        chapter: string;
        label: string;
        formula: null | string;
        otherCostType?: {
            uniqueId: string;
            name: string;
        };
    } = useWatch({ control, name: 'globalOtherCostId' });
    const currentName = useWatch({ control, name: 'name' });
    const currentCode = useWatch({ control, name: codeKey });
    const isDirtyOtherCode = dirtyFields?.key;

    const typeSelectDisabled = Boolean(globalOtherCost?.id);

    const isUer = useMemo(() => methodology === ProjectMethodologyEnum.Uer, [methodology]);

    const { isContractOtherCodeUnique } = useIsUniqueContractOtherCode({
        code: currentCode,
        isDirtyCode: isDirtyOtherCode,
        contractId,
    });

    useEffect(() => {
        if (isDirtyOtherCode && currentCode && !isContractOtherCodeUnique) {
            setError?.(codeKey, { message: 'Прочая с таким кодом уже существует' });
        } else {
            clearErrors?.(codeKey);
        }
    }, [clearErrors, currentCode, isContractOtherCodeUnique, isDirtyOtherCode, setError]);

    const chapterValue = useMemo(() => {
        if (isCreatingFromCatalog) {
            if (globalOtherCost) {
                return availableChapters[globalOtherCost?.chapter] || 'Лимиты Заказчика';
            }

            return '';
        }

        return availableChapters.CHAPTER9;
    }, [globalOtherCost, isCreatingFromCatalog]);

    const { deserializeFromBackend } = useEditorParseData({});

    useEffect(() => {
        if (globalOtherCost?.calculatingType === OtherCostCalculationTypeEnum.Percent) {
            setSelectedType(TYPE_VARIANTS[1]);

            setValue('formula', deserializeFromBackend(globalOtherCost?.formula));
        } else {
            setSelectedType(TYPE_VARIANTS[0]);

            setValue('formula', null);
        }
    }, [deserializeFromBackend, globalOtherCost?.calculatingType, globalOtherCost?.formula, setValue]);

    const [selectedConstructionTypes, setSelectedConstructionTypes] = useState<Record<string, boolean>>({});

    useEffect(() => {
        if (currentConstructionTypeId) {
            setSelectedConstructionTypes({
                [currentConstructionTypeId]: true,
            });
        }
    }, [currentConstructionTypeId]);

    const constructionTypeCheckHandler = useCallback(
        (id: number) =>
            ({ checked }: { checked: boolean }) => {
                setSelectedConstructionTypes((types) => ({
                    ...types,
                    [id]: checked,
                }));
            },
        [],
    );

    const [constructionTypesFromRedux] = useConstructionTypes(workOrderId, false);

    const actualConstructionTypes = useMemo(() => {
        const result = constructionTypes || constructionTypesFromRedux;

        if (!result) {
            return [];
        }

        return [...result].sort((a, b) => a.name.localeCompare(b.name));
    }, [constructionTypes, constructionTypesFromRedux]);

    const handleTabChange = useCallback(({ value }) => setActiveTab(value), [setActiveTab]);

    const {
        data: createOtherData,
        status: createOtherStatus,
        makeRequest: createOtherMakeRequest,
        reset: createOtherReset,
    } = useContractOtherCreate({
        contractId: Number(projectData?.contractId),
        methodology,
        creatingType,
        isCondition,
        selectedType,
        selectedChapterOrSection,
        workOrderId,
        workOrderStatus,
        workOrderObjectId,
        workOrderChangeId,
    });

    const spanSelectionData = useUerContractOtherCostLimitSpanSelectionList({
        withEffect: isUer && !noLimitSpan,
        contractId: Number(projectData?.contractId),
        constructionTypeId: currentConstructionTypeId,
    });

    const isRequestFetching = useMemo(() => createOtherStatus === RequestStatus.FETCHING, [createOtherStatus]);

    const resetData = useCallback(() => {
        createOtherReset(undefined);
        reset();
    }, [createOtherReset, reset]);

    useEffect(() => {
        if (createOtherData?.id && !isRequestFetching) {
            const { id } = createOtherData;

            resetData();
            handleClose?.();
            reloadHandler?.(id);
        }
    }, [createOtherData, createOtherData?.id, handleClose, isRequestFetching, reloadHandler, reset, resetData]);

    const createHandler = useCallback(() => {
        formRef.current?.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }, []);

    const { serializeStringForBackend } = useEditorParseData({});

    const isPercent = selectedType.id === OtherCostCalculationTypeEnum.Percent;

    const onSubmit = useCallback(
        (data) => {
            const hasName = Object.prototype.hasOwnProperty.call(data, 'name');
            const hasPercent = Object.prototype.hasOwnProperty.call(data, 'percent') && isPercent;

            if (
                (hasName && (data.name === undefined || data.name === null)) ||
                (!hasName && (data.globalOtherCostId === undefined || data.globalOtherCostId === null))
            ) {
                setActiveTab(tabsItems[0]);
                setTimeout(() => {
                    setError('name', requiredMessage);
                    setError('globalOtherCostId', requiredMessage);
                }, 0);

                return;
            }

            if (hasPercent && R.isNil(data.percent)) {
                setActiveTab(tabsItems[0]);
                setTimeout(() => setError('percent', requiredMessage));

                return;
            }

            if (hasPercent && replacerCost(data.percent) < 0) {
                setActiveTab(tabsItems[0]);
                setTimeout(() => setError('percent', { message: 'Процент должен быть не отрицательным числом' }));

                return;
            }

            const preparedData = isPercent ? data : R.omit(['percent'], data);
            const { limitsArray, haveError, errorMsgObj } = prepareLimitsData(
                selectedConstructionTypes,
                preparedData,
                isUer,
            );

            if (haveError) {
                setActiveTab(tabsItems[1]);

                if (errorMsgObj) {
                    Object.entries(errorMsgObj).forEach(([id, errorMessage]) => {
                        setError(`method.${id}`, { message: errorMessage as string });
                    });
                }

                return;
            }

            const serializedFormula = serializeStringForBackend(preparedData.formula);

            createOtherMakeRequest(preparedData, limitsArray, serializedFormula);

            sendGpnAnalyticsEvent({
                componentId: 'rim_other create button_pressed',
                projectId,
                addOtherActionType: creatingType,
                orderId: workOrderId ? Number(workOrderId) : undefined,
                orderStatus: workOrderStatus,
                projectName: projectData?.name,
                projectType: projectData?.contractType,
                projectDO: projectData?.subsidiary?.name,
                methodology: projectData?.methodology,
            });
        },
        [
            createOtherMakeRequest,
            creatingType,
            isPercent,
            isUer,
            projectData?.contractType,
            projectData?.methodology,
            projectData?.name,
            projectData?.subsidiary?.name,
            projectId,
            selectedConstructionTypes,
            sendGpnAnalyticsEvent,
            serializeStringForBackend,
            setError,
            workOrderId,
            workOrderStatus,
        ],
    );

    const handleMouseMoveSelect = useCallback(() => {
        setIsOpenTooltip(true);
    }, []);

    const handleMouseLeaveSelect = useCallback(() => {
        setIsOpenTooltip(false);
    }, []);

    const onSelectedChapterOrSectionChange = useCallback(({ value }) => setSelectedChapterOrSection(value), []);
    const onSelectedTypeChange = useCallback(({ value }) => setSelectedType(value), []);

    const onClose = useCallback(() => {
        if (isDirty) {
            setIsOpenWarningDialog(true);
        } else {
            resetData();
            handleClose();
        }
    }, [handleClose, isDirty, resetData]);

    const onCancelWarningDialog = useCallback(() => {
        setIsOpenWarningDialog(false);
    }, []);

    const onSubmitWarningDialog = useCallback(() => {
        onCancelWarningDialog();
        resetData();
        handleClose();
    }, [handleClose, onCancelWarningDialog, resetData]);

    const onNoOtherCostTypeErrorsClose = useCallback(() => {
        setNoOtherCostTypeErrorState(undefined);
        setValue('globalOtherCostId', undefined);
    }, [setValue]);

    const commonTabStyle = useMemo(
        () => (activeTab.id === TabsIds.COMMON ? undefined : invisibleStyle),
        [activeTab.id],
    );

    const labelInformerCreateOther = isCreatingFromCatalog
        ? 'Добавление прочих затрат из справочника методик расчета приведёт к обновлению справочника прочих затрат договора'
        : 'Добавление новых прочих затрат приведёт к обновлению справочника прочих затрат договора';

    const priceTabStyle = useMemo(() => (activeTab.id === TabsIds.PRICE ? undefined : invisibleStyle), [activeTab.id]);

    const formulaTabStyle = useMemo(
        () => (activeTab.id === TabsIds.FORMULA ? undefined : invisibleStyle),
        [activeTab.id],
    );

    const isFormulaFieldDisabled = useMemo(() => {
        if (isUer) {
            return selectedType.id !== OtherCostCalculationTypeEnum.Percent;
        }

        return (
            selectedType.id !== OtherCostCalculationTypeEnum.Percent ||
            globalOtherCost?.calculatingType === OtherCostCalculationTypeEnum.Percent
        );
    }, [globalOtherCost?.calculatingType, isUer, selectedType.id]);

    const isShowRationale = useMemo(
        () => !isCondition && !isUer && workOrderStatus === WorkOrderStatusEnum.InProgress,
        [isCondition, isUer, workOrderStatus],
    );

    const { items } = useGetContractOtherCostTypes({
        contractId,
        methodology,
    });

    useEffect(() => {
        if (globalOtherCost?.otherCostType?.uniqueId && items?.length) {
            const currentOtherCostType = items?.find((t) => t.uniqueId === globalOtherCost?.otherCostType?.uniqueId);

            setValue('otherCostType', currentOtherCostType);

            if (!currentOtherCostType) {
                setNoOtherCostTypeErrorState({
                    message: `Тип прочей "${globalOtherCost?.otherCostType?.name}" используемый выбранной прочей "${globalOtherCost?.label}" из справочника методик уже был назначен на другую прочую в договоре.\r\nЛибо скорректируйте назначенный тип, либо не используйте выбранную прочую из справочника методик`,
                });
            }
        }
    }, [globalOtherCost, items, setValue]);

    const [contractSeasons] = useUerContractSeasons(contractId, isUer);

    return (
        <ModalDialog
            isOpen
            title={title}
            onConfirm={createHandler}
            onClose={onClose}
            btnConfirmLabel="Создать"
            btnConfirmDisabled={!!errors?.name || !isContractOtherCodeUnique}
            withTitleCloseButton
            isCommit={isRequestFetching}
            style={{ width: 644 }}
            isOnClickOutsideDisable
        >
            <div className={!isCondition ? Styles.innerBlock : Styles.innerBlockCondition}>
                {!isCondition && (
                    <Informer
                        label={labelInformerCreateOther}
                        status="warning"
                        icon={IconWarning}
                        className={Styles.informer}
                    />
                )}
                <ControlledTabs
                    value={activeTab}
                    onChange={handleTabChange}
                    items={tabsItems}
                    view="bordered"
                    size="s"
                    className={Styles.tabs}
                    control={control}
                    fieldsToCheck={fieldsToCheck}
                />
                <FormProvider {...form}>
                    <form onSubmit={handleSubmit(onSubmit)} ref={formRef}>
                        <Grid style={commonTabStyle as React.CSSProperties}>
                            <GridItem className={Styles.marginBlock}>
                                {creatingType === CreatingTypes.MANUAL && (
                                    <ControlledTextField
                                        name="name"
                                        label="Наименование"
                                        placeholder="Введите"
                                        type="textarea"
                                        control={control}
                                        required
                                        rules={{
                                            required: 'Обязательное поле',
                                            maxLength: {
                                                value: 255,
                                                message: 'не более 255 символов',
                                            },
                                        }}
                                        isTooltipHidden={!!commonTabStyle}
                                    />
                                )}
                                {isCreatingFromCatalog && (
                                    <>
                                        <CreateOtherNameSelect
                                            control={control}
                                            contractId={projectData?.contractId}
                                            refTooltip={refTooltip}
                                            onMouseMoveSelect={handleMouseMoveSelect}
                                            onMouseLeaveSelect={handleMouseLeaveSelect}
                                            isTooltipHidden={!!commonTabStyle}
                                            methodology={methodology}
                                        />
                                        {!commonTabStyle && globalOtherCost?.label && isOpenTooltip && (
                                            <Tooltip size="l" direction="upCenter" anchorRef={refTooltip}>
                                                {globalOtherCost.label}
                                            </Tooltip>
                                        )}
                                    </>
                                )}
                            </GridItem>

                            {isUer && (
                                <GridItem className={Styles.marginBlock}>
                                    <ControlledTextField
                                        name="key"
                                        label="Код"
                                        placeholder="Введите"
                                        type="text"
                                        control={control}
                                        isTooltipHidden={!!commonTabStyle}
                                    />
                                </GridItem>
                            )}

                            <GridItem className={Styles.marginBlock}>
                                <div className="TextField TextField_labelPosition_left TextField_size_s TextField_view_default TextField_width_full">
                                    <div className="Text Text_lineHeight_m Text_size_s Text_view_secondary FieldLabel TextField-Label TextField-Label_labelPosition_left">
                                        Способ расчета
                                    </div>
                                    <RadioGroup
                                        name="type"
                                        direction="row"
                                        items={TYPE_VARIANTS}
                                        value={selectedType}
                                        getItemLabel={getItemLabel}
                                        onChange={onSelectedTypeChange}
                                        disabled={typeSelectDisabled || isCreatingFromCatalog}
                                        className={Styles.RadioGroup}
                                    />
                                </div>
                            </GridItem>
                            <GridItem className={isPercent ? Styles.marginBlock : undefined}>
                                {isPercent && (
                                    <ControlNumberField
                                        name="percent"
                                        label="Величина %"
                                        labelPosition="left"
                                        placeholder="%"
                                        control={control}
                                        size="s"
                                        required
                                        rules={{ required: 'Обязательное поле' }}
                                        className={Styles.percentInput}
                                        isTooltipHidden={!!commonTabStyle}
                                        decimalScale={isUer ? 2 : 3}
                                    />
                                )}
                            </GridItem>
                            {isCondition && !hasWorkOrders && (
                                <GridItem className={Styles.marginBlock}>
                                    <div className="TextField TextField_labelPosition_left TextField_size_s TextField_view_default TextField_width_full">
                                        <div className="Text Text_lineHeight_m Text_size_s Text_view_secondary FieldLabel TextField-Label TextField-Label_labelPosition_left">
                                            Раздел
                                        </div>
                                        <RadioGroup
                                            name="selectedChapterOrSection"
                                            direction="row"
                                            items={OTHER_SECTION_OR_CHAPTER}
                                            value={selectedChapterOrSection}
                                            getItemLabel={getItemLabel}
                                            onChange={onSelectedChapterOrSectionChange}
                                            className={Styles.RadioGroup}
                                        />
                                    </div>
                                </GridItem>
                            )}
                            {(!isCondition || hasWorkOrders) && (
                                <GridItem className={Styles.marginBlock}>
                                    <ControlledTextField
                                        name="chapter"
                                        label="Раздел"
                                        type="text"
                                        value={chapterValue}
                                        control={control}
                                        disabled
                                        placeholder=""
                                    />
                                </GridItem>
                            )}

                            <GridItem className={Styles.marginBlock}>
                                <OtherCostTypeSelect
                                    key={selectedType.id}
                                    control={control}
                                    isTooltipHidden={!!commonTabStyle}
                                    isDisabled={typeSelectDisabled || isCreatingFromCatalog}
                                />
                            </GridItem>

                            {isShowRationale && (
                                <GridItem>
                                    <ControlledTextField
                                        name="rationale"
                                        label="Обоснование"
                                        control={control}
                                        placeholder="Обоснование"
                                        type="textarea"
                                        cols={30}
                                        rows={3}
                                        rules={{ required: 'Обязательное поле' }}
                                        isTooltipHidden={!!commonTabStyle}
                                    />
                                </GridItem>
                            )}
                        </Grid>
                        <Grid style={priceTabStyle as React.CSSProperties}>
                            <GridItem
                                className={`${Styles.marginBlock} ${isUer ? Styles.labelBlock : Styles.bimLabelBlock}`}
                            >
                                <span className={!isUer ? Styles.constructionTypeBimLabel : undefined}>
                                    Вид строительства
                                </span>
                                <span className={!isCondition && isUer ? Styles.priceLabel : undefined}>
                                    Сумма, руб. без НДС
                                </span>
                                {isUer && (
                                    <span className={!isCondition ? Styles.typeLabel : undefined}>
                                        Способ распределения
                                    </span>
                                )}
                            </GridItem>
                            {actualConstructionTypes?.map(({ id, name }) => {
                                const defaultConstruction = id === currentConstructionTypeId;
                                const isDisabled = !selectedConstructionTypes[id];
                                const isChecked = selectedConstructionTypes[id] || false;
                                const costRounding =
                                    projectData?.contractNavigation?.data?.contractNavData?.metadata?.costRounding ?? 0;

                                return (
                                    <GridItem key={id} className={Styles.marginBlock}>
                                        <div className={isUer ? Styles.labelBlock : Styles.flexCenter}>
                                            <div className={Styles.checkboxWrapper}>
                                                <Checkbox
                                                    label={name}
                                                    id={String(id)}
                                                    onChange={constructionTypeCheckHandler(id)}
                                                    checked={isChecked}
                                                    disabled={defaultConstruction}
                                                    className={isUer ? Styles.checkbox : undefined}
                                                />
                                                {defaultConstruction && <span className="FieldLabel-Star">*</span>}
                                                {isChecked && !defaultConstruction ? (
                                                    <span className="FieldLabel-Star">*</span>
                                                ) : undefined}
                                            </div>
                                            {!isUer && (
                                                <div className={Styles.textInputBim}>
                                                    <ControlNumberField
                                                        name={`cost.${id}`}
                                                        labelPosition="left"
                                                        control={control}
                                                        type="text"
                                                        size="s"
                                                        rules={{
                                                            validate: validateLimitCost({
                                                                isSelected: isChecked,
                                                            }),
                                                        }}
                                                        disabled={isDisabled}
                                                        isTooltipHidden={!!priceTabStyle || isDisabled}
                                                        decimalScale={0}
                                                        hideOnDisabled
                                                        placeholder={isDisabled ? '' : 'Введите'}
                                                    />
                                                </div>
                                            )}
                                            {isUer && (
                                                <>
                                                    <ContractOtherCostLimitSpan
                                                        name={currentName}
                                                        control={control}
                                                        costRounding={costRounding}
                                                        defaultConstruction={defaultConstruction}
                                                        constructionTypeId={id}
                                                        constructionTypeName={name}
                                                        isDisabled={isDisabled}
                                                        isChecked={isChecked}
                                                        isTooltipHidden={!!priceTabStyle || isDisabled}
                                                        setValue={setValue}
                                                        noLimitSpan={noLimitSpan}
                                                        inputClassName={
                                                            noLimitSpan
                                                                ? Styles.contractOtherCostLimitNoSpanInput
                                                                : Styles.contractOtherCostLimitSpanInput
                                                        }
                                                        spanSelectionData={spanSelectionData}
                                                    />
                                                    <FieldGroup size="s" className={Styles.seasonSelect}>
                                                        <ControlledSelectField
                                                            name={`method.${id}`}
                                                            control={control}
                                                            placeholder={isDisabled ? '' : 'Выберите'}
                                                            items={seasonItems}
                                                            className={Styles.seasonSelect}
                                                            required={defaultConstruction}
                                                            rules={{
                                                                required: isChecked && 'Обязательное поле',
                                                            }}
                                                            disabled={isDisabled}
                                                            clearOnDisabled
                                                            isTooltipHidden={!!priceTabStyle}
                                                            tooltipDirection="downCenter"
                                                            dropdownClassName={Styles.dropdownSelect}
                                                        />
                                                        <OtherManualSeasonDistribution
                                                            control={control}
                                                            constructionTypeName={name}
                                                            constructionTypeId={id}
                                                            costRounding={costRounding}
                                                            isDisabled={isDisabled}
                                                            clearErrors={clearErrors}
                                                            seasons={contractSeasons}
                                                        />
                                                    </FieldGroup>
                                                </>
                                            )}
                                        </div>
                                    </GridItem>
                                );
                            })}
                        </Grid>

                        <GridItem style={formulaTabStyle as React.CSSProperties}>
                            {isPercent ? (
                                <FormulaEditor
                                    name="formula"
                                    control={control}
                                    getObjectSourceQuery={getOtherFormulaSourceObjectGql}
                                    isDisabled={isFormulaFieldDisabled}
                                    tooltipTitle="Формула расчета"
                                    isRequired={!isFormulaFieldDisabled}
                                    rules={{
                                        required: !isFormulaFieldDisabled && 'Обязательное поле',
                                    }}
                                    isTooltipHidden={!!formulaTabStyle}
                                />
                            ) : (
                                <Text view="secondary" as="span" className={Styles.formulaUnavailableText}>
                                    Для способа расчета &quot;Расчет&quot; ввод формул недоступен
                                </Text>
                            )}
                        </GridItem>
                    </form>
                </FormProvider>
            </div>
            <ConfirmationDialog
                textHeader="Отмена создания прочих затрат"
                textConfirmation="Вы уверены, что хотите отменить? Заполненные данные не будут сохранены"
                confirmButtonText="Отменить"
                cancelButtonText="Не отменять"
                onSubmit={onSubmitWarningDialog}
                isOpened={isOpenWarningDialog}
                onClose={onCancelWarningDialog}
            />
            <UploadStatus
                isOpened={!!noOtherCostTypeErrorState}
                handleCloseModal={onNoOtherCostTypeErrorsClose}
                data={noOtherCostTypeErrorState}
                titleText={title}
                status="error"
                btnText="Понятно"
            />
        </ModalDialog>
    );
};

CreateOtherModal.displayName = 'CreateOtherModal';
